import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { ReactComponent as CalendarV2Icon } from '../../assets/icons/calender-v2-icon.svg';
import { ReactComponent as CalendarIcon } from '../../assets/icons/calender.svg';
import { ReactComponent as ClockIcon } from '../../assets/icons/clock-icon.svg';
import { ReactComponent as CloseIcon } from '../../assets/icons/close-circle.svg';
import { ReactComponent as DocumentFileIcon } from '../../assets/icons/document.svg';
import { ReactComponent as EditPencilCard } from '../../assets/icons/edit-pencil.svg';
import { ReactComponent as EyeIcon } from '../../assets/icons/eye-fill-icon.svg';
import { ReactComponent as ArabicFlag } from '../../assets/icons/flag-icons/arabic.svg';
import { ReactComponent as EnglishFlag } from '../../assets/icons/flag-icons/english.svg';
import { ReactComponent as FrenchFlag } from '../../assets/icons/flag-icons/french.svg';
import { ReactComponent as SpanishFlag } from '../../assets/icons/flag-icons/spanish.svg';
import { ReactComponent as GlobeIcon } from '../../assets/icons/globe-icon.svg';
import { ReactComponent as LightningIcon } from '../../assets/icons/lightning-bolt-icon.svg';
import { ReactComponent as LocationIcon } from '../../assets/icons/location-icon.svg';
import { ReactComponent as LocationSelector } from '../../assets/icons/location-selector.svg';
import { ReactComponent as ArrowRight } from '../../assets/icons/next-arrow.svg';
import { ReactComponent as PhoneIcon } from '../../assets/icons/phone-icon.svg';
import { ReactComponent as ExpandIcon } from '../../assets/icons/tables/expand-icon.svg';
import { ReactComponent as CheckCircleIcon } from '../../assets/icons/tick-circle-icon.svg';
import DefaultStationImage from '../../assets/images/default-station.png';
import { appCurrentBranding, brandIcons } from '../../config/branding';
import { branding, PortalType } from '../../config/enum';
import { getTimeZones, getUserTimeZone, normalizeTimeZone } from '../../config/timezonesLabel';
import { useGoogleMaps } from '../../contexts/google-map-context/GoogleMapContent';
import { usePortal } from '../../contexts/PortalContext/PortalContext';
import {
  NewProgressProps as NewProgressProperties,
  SingleFileUploadProgress,
} from '../../interface/file-upload-stream.interface';
import {
  Amenity,
  AvailableStation,
} from '../../interface/partner-interfaces/station-management.interface';
import AddFacilityAccount from '../../pages/account-management/components/add-facility-account';
import AmenitiesInput from '../../pages/station-pages/station-management/components/AmenitiesInput';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  setUnUploadedFileData,
  updateFileUploadProgress,
} from '../../redux/slices/FileUploadStreamSlice';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { fetchApprovedStations } from '../../redux/slices/Partners/ApprovedStationManagementSlice';
import {
  fetchPendingNotification,
  fetchPendingStations,
} from '../../redux/slices/Partners/PendingStationManagementSlice';
import { fetchRejectedStations } from '../../redux/slices/Partners/RejectedStationManagementSlice';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../shared-functions/ConvertUTCtoDateTime';
import { convertTo12HourFormat } from '../../shared-functions/DataTime';
import { permissionCheck } from '../../shared-functions/JWTDocodeFunction';
import { SanitizeFileName } from '../../shared-functions/StringFunctions';
import {
  checkFileSizeValidation,
  // checkEditFileSizeValidation,
  longTextValidation,
  stringValidation,
} from '../../shared-functions/Validation';
import deviceApi from '../../utils/api/partner/device-partner';
import chargingStation from '../../utils/api/partner/station';
import BatteryLoader from '../battery-loader/BatteryLoader';
import MyButton from '../button/Button';
import CustomAddressInput from '../custom-address-input/CustomAddressInput';
import CustomFileInput from '../custom-file-input/CustomFileInput';
import CustomInput from '../custom-input/CustomInput';
import CustomPhoneInput from '../custom-input/CustomPhoneInput';
import CustomInputSwitch from '../custom-input-switch/CustomInputSwitch';
import CustomMediaUpload from '../custom-media-upload/CustomMediaUpload';
import CustomTable from '../custom-table/CustomTable';
import CustomTimePicker from '../custom-time-input/CustomTimePicker';
import FileUploadProgress from '../file-upload-progress/FileUploadProgress';
import ModalBox from '../modal-box/ModalBox';
import SelectField from '../select-field/SelectField';
import SelectLatLong from '../select-location/components/SelectLatLong';
import SelectLocation from '../select-location/SelectLocation';
import SuccessNotification from '../success-notification/SuccessNotification';
import TabToggle from '../tab-toggle/TabToggle';

// eslint-disable-next-line import/no-cycle
import DeviceSidePanel from './DeviceSidePanel';
// import { TooltipIcon } from '../tooltip/TooltipIcon';

interface StationSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
  initialIsEditMode?: boolean;
  onEditModeChange?: (isEditMode: boolean) => void;
  onDeleteStation?: (stationId: string) => void;
  onSuccess?: () => void;
  allowList?: boolean;
  onClose?: () => void;
}

interface Connectors {
  connectorType?: string[] | string;
}

interface ChargingPointProperties {
  chargingPointSn: string;
  connectorType?: string;
  isDeviceOffline: boolean;
  connectors?: Connectors[];
  _id: string;
  id: string;
}

interface DayOperatingHours {
  status: 'open' | 'closed';
  open?: string;
  close?: string;
}
interface OperatingHours {
  allDay: boolean;
  monday: DayOperatingHours;
  tuesday: DayOperatingHours;
  wednesday: DayOperatingHours;
  thursday: DayOperatingHours;
  friday: DayOperatingHours;
  saturday: DayOperatingHours;
  sunday: DayOperatingHours;
}

interface InputInterface {
  label: string;
  value: string;
  description: string;
}

interface AddDeviceProperties {
  _id: string;
  accountCustomId: string;
  accountName: string;
}

type DayKey = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

/**
 * View the file based on its type
 * @param {object} param0 - The parameters
 * @param {any} param0.file - The file to preview
 * @param {number} param0.extraCount - Extra count of files
 * @param {Function} param0.onClick - Click handler
 * @returns {any} file preview
 */
function FilePreview({
  file,
  extraCount = 0,
  onClick,
}: {
  file: any;
  extraCount?: number;
  /**
   * onClick
   */
  onClick?: () => void;
}) {
  const { t } = useTranslation('sidePanel');

  let fileTypeView;

  if (file.fileType.includes('image')) {
    fileTypeView = (
      <img
        src={file.signedUrl}
        alt=""
        className="selected-image"
        onClick={onClick}
        onKeyDown={(even) => {
          if (even.key === 'Enter' && onClick) onClick();
        }}
        role="presentation"
        style={{ cursor: onClick ? 'pointer' : 'default' }}
      />
    );
  } else if (file.fileType.includes('video')) {
    fileTypeView = (
      <video controls className="selected-video">
        <source src={file.signedUrl} type={file.fileType} />
        <track src="path/to/captions.vtt" kind="captions" label="English captions" default />
        {t('browserNotVideoTag')}{' '}
      </video>
    );
  }

  return (
    <div className="single-selected-wrap">
      {fileTypeView}
      {extraCount && extraCount > 0 ? (
        <div className="extra-count-overlay">
          <span>+{extraCount}</span>
        </div>
      ) : null}
    </div>
  );
}
FilePreview.defaultProps = {
  extraCount: 0,
  onClick: undefined,
};

/**
 * Get language options with fresh JSX element flags
 * @returns {any} - Array of language options
 */
const getLanguageOptions = () => [
  {
    value: 'English',
    label: 'English',
    labelNode: (
      <div className="language-option-node">
        <EnglishFlag style={{ width: 24, height: 16, borderRadius: 2 }} />
        <span>English</span>
      </div>
    ),
  },
  {
    value: 'Arabic',
    label: 'Arabic',
    labelNode: (
      <div className="language-option-node">
        <ArabicFlag style={{ width: 24, height: 16, borderRadius: 2 }} />
        <span>Arabic</span>
      </div>
    ),
  },
  {
    value: 'French',
    label: 'French',
    labelNode: (
      <div className="language-option-node">
        <FrenchFlag style={{ width: 24, height: 16, borderRadius: 2 }} />
        <span>French</span>
      </div>
    ),
  },
  {
    value: 'Spanish',
    label: 'Spanish',
    labelNode: (
      <div className="language-option-node">
        <SpanishFlag style={{ width: 24, height: 16, borderRadius: 2 }} />
        <span>Spanish</span>
      </div>
    ),
  },
];

/**
 * Station side panel
 * @param {any} properties properties
 * @returns {any} component
 */
function StationSidePanel(properties: Readonly<StationSidePanelProperties>) {
  const { t, i18n } = useTranslation(['sidePanel', 'station']);
  const navigate = useNavigate();
  const [actionLoading, setActionLoading] = useState(false);
  const [accountNameList, setAccountNameList] = useState<InputInterface[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');

  /**
   * Handle close device details overlay
   */
  const handleCloseDeviceOverlay = () => {
    setSelectedDeviceId('');
  };
  const {
    fetchIdFrom,
    id,
    onEditModeChange,
    onSuccess,
    initialIsEditMode,
    allowList = true,
    onClose,
  } = properties;
  const { status: parameterStatus, page } = useParams();
  const status = parameterStatus || page;
  const dispatch = useAppDispatch();
  const [isEdit, setIsEdit] = useState(false);
  const [editData, setEditData] = useState<any>({
    name: '',
    address: '',
    accountCustomId: '',
    electricityContractType: '',
    StationLocation: '',
    editLandOwnerShipDocument: [],
    editEbReport: [],
    editMedias: [],
    removedMedias: [],
    amenities: [],
    timeZone: getUserTimeZone(),
    operatingHours: {
      allDay: true,
      monday: { isOpen: true, from: '00:00', to: '23:59' },
      tuesday: { isOpen: true, from: '00:00', to: '23:59' },
      wednesday: { isOpen: true, from: '00:00', to: '23:59' },
      thursday: { isOpen: true, from: '00:00', to: '23:59' },
      friday: { isOpen: true, from: '00:00', to: '23:59' },
      saturday: { isOpen: true, from: '00:00', to: '23:59' },
      sunday: { isOpen: true, from: '00:00', to: '23:59' },
    },
    isGuestChargingExclusive: false,
    countryCode: appCurrentBranding === branding.VOLTIE ? '1' : '966',
    geoLocations: [],
  });
  const { portalType } = usePortal();
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [chargingStationData, setChargingStationData] = useState<AvailableStation>();
  /**
   * Access Partner
   * @param {any} type - The type
   */
  const expandFunction = (type?: string) => {
    if (!chargingStationData) return;

    if (type === 'new') {
      const url = `/ev-admin/station-expanded/${chargingStationData._id}/station-dashboard?searchBy=${chargingStationData.name}&source=${window.location.pathname}`;
      window.open(url, '_blank');
    } else {
      navigate(
        {
          pathname: `/ev-admin/station-management/${chargingStationData._id}/station-dashboard`,
          search: `?searchBy=${chargingStationData.name}`,
        },
        { state: { source: window.location.pathname } },
      );
    }
  };
  const [chargingPointData, setChargingPointData] = useState<ChargingPointProperties[]>([]);
  const [isLoader, setIsLoader] = useState<boolean>(false);
  // const [partLength, setPartLength] = useState<number>(1);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{
    url: string;
    type: string;
    name: string;
  } | null>(null);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<any>({
    title: '',
    content: null,
  });
  const [formStatus, setFormStatus] = useState({
    buttonLoader: false,
  });
  const [latLongError, setLatLongError] = useState<string>('');
  const [dynamicAmenities, setDynamicAmenities] = useState<Amenity[]>([]);
  const [isFetchingAmenities, setIsFetchingAmenities] = useState(false);
  const { isLoaded } = useGoogleMaps();

  const { page: approvedPage, limit: approvedLimit } = useAppSelector(
    (state) => state.approvedStation,
  );
  const { page: rejectedPage, limit: rejectedLimit } = useAppSelector(
    (state) => state.partnersRejectStation,
  );
  const [rejectReason, setRejectReason] = useState('');
  const [locationPopup, setLocationPopup] = useState<'open' | 'close'>('close');
  // const [priceRequestData, setPriceRequestData] = useState<ElectricityPrice | null>(null);
  const [localUsageCostView, setLocalUsageCostView] = useState<
    'pending' | 'rejectForm' | 'approved' | 'rejected'
  >('pending');

  const [openTab, setOpenTab] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<string>('details');
  const [electricityCostTouched, setElectricityCostTouched] = useState<boolean>(false);

  const [additionalDocuments, setAdditionalDocuments] = useState<File[][]>([]);
  const [navigationDirections, setNavigationDirections] = useState<
    { language: string; direction: string }[]
  >([{ language: '', direction: '' }]);

  /**
   * Filter and return language options not already selected
   * @param {any} currentIndex - The current index of the dropdown
   */
  // eslint-disable-next-line unicorn/consistent-function-scoping
  const getAvailableLanguageOptions = (currentIndex: number) => {
    const selectedLangs = navigationDirections
      .map((item: any, index: number) => (index !== currentIndex ? item.language : ''))
      .filter(Boolean);
    return getLanguageOptions().filter((option) => !selectedLangs.includes(option.value));
  };

  /**
   * Handle direction text change and enforce language characters
   * @param {any} index - The index of the row being changed
   * @param {any} value - The input text value
   */
  // eslint-disable-next-line unicorn/consistent-function-scoping
  const handleDirectionsChange = (index: number, value: string) => {
    const lang = navigationDirections[index].language;
    let filteredValue = value;
    if (lang === 'English') {
      filteredValue = value.replace(/[^a-zA-Z0-9\s.,'()"\-_?!]/g, '');
    } else if (lang === 'Arabic') {
      filteredValue = value.replace(
        /[^\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF0-9\s.,'()"\-_?!]/g,
        '',
      );
    } else if (lang === 'French') {
      filteredValue = value.replace(/[^a-zA-ZÀ-ÿ0-9\s.,'()"\-_?!]/g, '');
    } else if (lang === 'Spanish') {
      filteredValue = value.replace(/[^a-zA-ZáéíóúüñÁÉÍÓÚÜÑ0-9\s.,'()"\-_?!]/g, '');
    }
    const updated = [...navigationDirections];
    updated[index].direction = filteredValue;
    setNavigationDirections(updated);
  };

  /**
   * Add a new navigation direction row
   */
  // eslint-disable-next-line unicorn/consistent-function-scoping
  const handleAddNavigationDirection = () => {
    if (navigationDirections.length < getLanguageOptions().length) {
      setNavigationDirections([...navigationDirections, { language: '', direction: '' }]);
    }
  };

  /**
   * Change tab + update URL
   * @param {any} tabLabel tab label
   */
  const changeTabPages = (tabLabel: string) => {
    const index = [t('stationInformation'), t('operatingHours')].indexOf(tabLabel);
    if (index !== -1) {
      setOpenTab(index);
    }
  };

  const timeZoneOptions = React.useMemo(() => getTimeZones(), []);

  /**
   * Handle operating hours change
   * @param {any} day day
   * @param {any} field field
   * @param {any} value value
   */
  const handleOperatingHoursChange = (
    day: DayKey,
    field: 'isOpen' | 'from' | 'to' | 'is24Hours',
    value: any,
  ) => {
    setEditData((previous: any) => {
      if (!previous) return previous;
      const updatedHours = {
        ...previous.operatingHours!,
        [day]: {
          ...(previous.operatingHours as any)?.[day],
          [field]: value,
        },
      };

      const daysList = [
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday',
      ] as const;

      const isAllDayOpen = daysList.every((d) => {
        const dData = updatedHours[d];
        return (
          dData?.isOpen &&
          (dData?.is24Hours || (dData?.from === '00:00' && dData?.to === '23:59'))
        );
      });

      updatedHours.allDay = isAllDayOpen;

      return {
        ...previous,
        operatingHours: updatedHours as OperatingHours,
      };
    });
  };

  /**
   * Handle All Day toggle
   * @param {any} name name
   * @param {any} value value
   */
  const handleAllDayToggle = (name: string, value: boolean) => {
    setEditData((previous: any) => {
      if (!previous) return previous;
      const updatedOperatingHours = { ...previous.operatingHours! };
      updatedOperatingHours.allDay = value;

      if (value) {
        const fullDay = { isOpen: true, from: '00:00', to: '23:59', is24Hours: true };
        updatedOperatingHours.monday = fullDay;
        updatedOperatingHours.tuesday = fullDay;
        updatedOperatingHours.wednesday = fullDay;
        updatedOperatingHours.thursday = fullDay;
        updatedOperatingHours.friday = fullDay;
        updatedOperatingHours.saturday = fullDay;
        updatedOperatingHours.sunday = fullDay;
      } else {
        const customDay = { isOpen: true, from: '09:00', to: '18:00', is24Hours: false };
        updatedOperatingHours.monday = customDay;
        updatedOperatingHours.tuesday = customDay;
        updatedOperatingHours.wednesday = customDay;
        updatedOperatingHours.thursday = customDay;
        updatedOperatingHours.friday = customDay;
        updatedOperatingHours.saturday = customDay;
        updatedOperatingHours.sunday = customDay;
      }

      return {
        ...previous,
        operatingHours: updatedOperatingHours as OperatingHours,
      };
    });
  };

  /**
   * Handle Day Toggle
   * @param {any} day day
   * @param {any} isOpen is open
   */
  const handleDayToggle = (day: DayKey, isOpen: boolean) => {
    handleOperatingHoursChange(day, 'isOpen', isOpen);
  };

  /**
   * Handle open device side panel
   * @param {string} _ - Unused parameter
   * @param {ChargingPointProperties} row - The row data representing the charging point
   */
  const handleOpenDeviceSidePanel = (_: string, row: ChargingPointProperties) => {
    if (!row?._id) return;
    setSelectedDeviceId(row._id);
  };

  /**
   * Handle view document
   * @param {any} file - The file
   */
  const handleViewDocument = (file: any) => {
    if (file?.signedUrl) {
      setSelectedDocument({
        url: file.signedUrl,
        type: file.fileType || 'application/pdf', // Fallback or logic
        name: file.fileName || 'Document',
      });
      setIsDocumentModalOpen(true);
    }
  };

  /**
   * Render status badge based on station status.
   * @returns {React.ReactElement} Status badge component
   */
  const renderStatusBadge = () => {
    const activeStatus = status || chargingStationData?.submission?.status;

    if (activeStatus === 'approved') {
      return (
        <span className="badge-active">
          <span className="badge-dot" />
          {t('active', 'Active')}
        </span>
      );
    }

    if (activeStatus === 'rejected') {
      return (
        <span className="badge-rejected">
          <span className="badge-dot" />
          {t('rejected', 'Rejected')}
        </span>
      );
    }

    return (
      <span className="badge-pending">
        <span className="badge-dot" />
        {t('pending', 'Pending')}
      </span>
    );
  };

  const amenitiesDetails = [
    {
      name: t('gasStation'),
      key: 'Gas Station',
      svgIconActive: <brandIcons.GasStationActive />,
    },
    {
      name: t('wifi'),
      key: 'Free Wifi',
      svgIconActive: <brandIcons.WifiActive />,
    },
    {
      name: t('restroom'),
      key: 'Restroom',
      svgIconActive: <brandIcons.RestroomActive />,
    },
    {
      name: t('lounge'),
      key: 'Lounge',
      svgIconActive: <brandIcons.LoungeActive />,
    },
    {
      name: t('cafe'),
      key: 'Cafe',
      svgIconActive: <brandIcons.CaffeeActive />,
    },
    {
      name: t('shopping'),
      key: 'Shopping',
      svgIconActive: <brandIcons.ShoppingActive />,
    },
    {
      name: t('restaurant'),
      key: 'Restaurant',
      svgIconActive: <brandIcons.RestaurantActive />,
    },
    {
      name: t('relaxingArea'),
      key: 'Relaxing Area',
      svgIconActive: <brandIcons.RelaxingActive />,
    },
    // {
    //   name: 'Locker',
    //   key: 'Locker',
    //   svgIconActive: <brandIcons.LockerActive />,
    // },
    // {
    //   name: 'Work Shop',
    //   key: 'Work Shop',
    //   svgIconActive: <brandIcons.WorkshopActive />,
    // },
    {
      name: t('carWash'),
      key: 'Car Wash',
      svgIconActive: <brandIcons.CarwashActive />,
    },
  ];

  const column = [
    {
      label: t('device'),
      accessorKey: 'chargingPointSn' as keyof ChargingPointProperties,
      sortAllow: false,
      columnWidth: 80,

      /**
       * Render device name
       * @param {any} row row data
       * @returns {any} charging point
       */
      customData: (row: ChargingPointProperties) => (
        <p className="table-row-each-data hyperlink">{row.chargingPointSn}</p>
      ),
    },
    {
      label: t('status'),
      accessorKey: 'isDeviceOffline' as keyof ChargingPointProperties,
      sortAllow: false,
      columnWidth: 80,
      /**
       * Function
       * @param {any} row row data
       * @returns {any} online status
       */
      customData: (row: ChargingPointProperties) => (
        <p className={`${row.isDeviceOffline ? 'offline' : 'online'}`}>
          {row.isDeviceOffline ? t('offline') : t('online')}
        </p>
      ),
    },
    {
      label: t('connectorType'),
      accessorKey: 'connectorType' as keyof ChargingPointProperties,
      sortAllow: false,
      columnWidth: 80,
      /**
       * Rendering connector type
       * @param {ChargingPointProperties} currentRow - Current row data
       * @returns {any} Formatted connector type text
       */
      customData: (currentRow: ChargingPointProperties) => {
        const types = currentRow?.connectors
          ?.flatMap((connector) =>
            Array.isArray(connector?.connectorType)
              ? connector.connectorType
              : [connector?.connectorType],
          )
          .filter(Boolean);

        return <span>{types && types.length > 0 ? types.join(', ') : '-'}</span>;
      },
    },
  ];

  /**
   * Function to fetch station details
   * @param {any} stationId station id
   */
  const handleFetchStationDetails = async (stationId: string) => {
    setIsLoader(true);
    chargingStation
      .fetchChargingStationByStationId(stationId)
      .then((response) => {
        setChargingStationData(response?.data);
        setIsLoader(false);
      })
      .catch((error) => {
        setIsLoader(false);
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || 'Something went wrong!',
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  /**
   * Function to fetch station details
   * @param {any} stationId station id
   */
  const handleFetchDeviceDetails = async (stationId: string) => {
    chargingStation
      .fetchChargingPointByStationId(stationId)
      .then((response) => {
        setChargingPointData(response.data);
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || 'Something went wrong!',
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  /**
   * Function to render document chip
   * @param {object} param0 - The parameters
   * @param {string} param0.label - The label of the document
   * @param {any} param0.file - The file object
   * @param {Function} param0.onClick - The click handler function
   * @returns {any} JSX.Element
   */
  function DocumentChip({
    label,
    file = undefined,
    onClick,
  }: {
    label: string;
    file?: any;
    onClick: () => void;
  }) {
    return (
      <div className="document-chip-wrap">
        <span className="doc-label">{label === 'ebReports' ? t('ebReports') : label}</span>
        <div className="doc-file-box">
          <div className="doc-icon-name">
            <DocumentFileIcon />
            <span className="file-name">{file ? file.fileName : t('noFile')}</span>
          </div>
          {file && (
            <div className="doc-view-icon" role="presentation" onClick={onClick}>
              <EyeIcon />
            </div>
          )}
        </div>
      </div>
    );
  }
  DocumentChip.defaultProps = {
    file: undefined,
  };

  const electricityContractType = [
    {
      label: t('standardResidential'),
      value: 'standard-residential',
    },
    {
      label: t('commercial'),
      value: 'commercial',
    },
    {
      label: t('industrial'),
      value: 'industrial',
    },
    {
      label: t('evChargingSpecificContracts'),
      value: 'ev-charging-specific-contracts',
    },
  ];

  /**
   * Handle close document modal
   */
  const handleCloseDocumentModal = () => {
    setIsDocumentModalOpen(false);
    setSelectedDocument(null);
  };

  /**
   * Handle Input Change
   * @param {any} name - The name
   * @param {any} value - The value
   */
  const handleInputChange = (name: string, value: string | number | any) => {
    if (name === 'governmentId') {
      setEditData((previous: any) => ({
        ...previous,
        [name]: value,
        governmentIdOld: [],
      }));
    } else {
      setEditData((previous: any) => ({
        ...previous,
        [name]: value,
      }));
    }
  };

  /**
   * Handle Change address
   * @param {any} name - The name
   * @param {any} address - The address
   * @param {any} latitude - The latitude
   * @param {any} longitude - The longitude
   * @param {any[]} geoLocations - The geoLocations
   */
  const handleChangeAddress = (
    name: string,
    address: string | number,
    latitude: number | null,
    longitude: number | null,
    geoLocations: any[],
  ) => {
    setEditData({
      ...editData,
      [name]: address,
      latitude,
      longitude,
      geoLocations,
    });
    setLatLongError('');
  };

  /**
   * Handle lat & long change
   * @param {number} latitude - The latitude value
   * @param {number} longitude - The longitude value
   * @param {string} [address] - The selected address value
   * @param {any[]} [geoLocation] - The selected geo location values
   */
  const handleChangeLatLong = (
    latitude: number,
    longitude: number,
    address?: string,
    geoLocation?: any[],
  ) => {
    setEditData((previousValue: any) => ({
      ...previousValue,
      latitude,
      longitude,
      address: address ?? previousValue.address,
      geoLocations: geoLocation ?? previousValue.geoLocations,
    }));
    setLatLongError('');
  };

  useEffect(() => {
    if (isEdit && editData.latitude && editData.longitude && isLoaded && window.google) {
      setIsFetchingAmenities(true);

      const mapLocation = new window.google.maps.LatLng(
        parseFloat(String(editData.latitude)),
        parseFloat(String(editData.longitude)),
      );

      const service = new window.google.maps.places.PlacesService(document.createElement('div'));

      service.nearbySearch(
        {
          location: mapLocation,
          radius: 1000,
        },
        (results, placesStatus) => {
          if (placesStatus === globalThis.google.maps.places.PlacesServiceStatus.OK && results) {
            const fetchedAmenities: Amenity[] = [];
            results.forEach((place) => {
              const types = place.types || [];
              if (types.includes('gas_station') && !fetchedAmenities.includes('Gas Station'))
                fetchedAmenities.push('Gas Station');
              if (
                (types.includes('cafe') || types.includes('library')) &&
                !fetchedAmenities.includes('Free Wifi')
              )
                fetchedAmenities.push('Free Wifi');
              if (
                (types.includes('gas_station') ||
                  types.includes('shopping_mall') ||
                  types.includes('restaurant')) &&
                !fetchedAmenities.includes('Restroom')
              )
                fetchedAmenities.push('Restroom');
              if (
                (types.includes('lodging') ||
                  types.includes('transit_station') ||
                  types.includes('airport')) &&
                !fetchedAmenities.includes('Lounge')
              )
                fetchedAmenities.push('Lounge');
              if (
                (types.includes('cafe') || types.includes('bakery')) &&
                !fetchedAmenities.includes('Cafe')
              )
                fetchedAmenities.push('Cafe');
              if (
                (types.includes('shopping_mall') ||
                  types.includes('store') ||
                  types.includes('supermarket') ||
                  types.includes('convenience_store')) &&
                !fetchedAmenities.includes('Shopping')
              )
                fetchedAmenities.push('Shopping');
              if (
                (types.includes('restaurant') || types.includes('meal_takeaway')) &&
                !fetchedAmenities.includes('Restaurant')
              )
                fetchedAmenities.push('Restaurant');
              if (
                (types.includes('park') || types.includes('spa') || types.includes('campground')) &&
                !fetchedAmenities.includes('Relaxing Area')
              )
                fetchedAmenities.push('Relaxing Area');
              if (
                (types.includes('storage') || types.includes('transit_station')) &&
                !fetchedAmenities.includes('Locker')
              )
                fetchedAmenities.push('Locker');
              if (types.includes('car_repair') && !fetchedAmenities.includes('Work Shop'))
                fetchedAmenities.push('Work Shop');
              if (types.includes('car_wash') && !fetchedAmenities.includes('Car Wash'))
                fetchedAmenities.push('Car Wash');
            });
            setDynamicAmenities(fetchedAmenities);
            setEditData((previous: any) => ({
              ...previous,
              amenities: fetchedAmenities,
            }));
          } else {
            setDynamicAmenities([]);
            setEditData((previous: any) => ({
              ...previous,
              amenities: [],
            }));
          }
          setIsFetchingAmenities(false);
        },
      );
    }
  }, [isEdit, editData.latitude, editData.longitude, isLoaded]);

  /**
   * Function to remove media
   * @param {any} name - name of the media
   * @param {any} index - index of the media
   * @param {any} url - url of the media
   * @param {any} type - type of the media
   */
  const handleRemoveMedias = (
    name: string,
    index: number,
    url: string,
    type: 'newFile' | 'existingFile',
  ) => {
    if (type === 'newFile') {
      setEditData((previous: any) => ({
        ...previous,
        editMedias: previous.editMedias.filter((_: any, index_: number) => index_ !== index),
      }));
    }

    if (type === 'existingFile') {
      setEditData((previous: any) => ({
        ...previous,
        medias: (chargingStationData?.medias || []).filter((m: any) => m.url !== url),
        removedMedias: [...(previous.removedMedias || []), url],
      }));
      setChargingStationData((previous: any) => ({
        ...previous,
        medias: (previous?.medias || []).filter((m: any) => m.url !== url),
      }));
    }
  };
  /**
   * Populate edit data
   */
  const handlePopulateEditData = () => {
    let phoneNumber = chargingStationData?.emergencyContact || '';
    let cCode = appCurrentBranding === branding.VOLTIE ? '1' : '966';

    if (phoneNumber.startsWith('+')) {
      const cleaned = phoneNumber.slice(1);
      const possibleCodes = ['966', '971', '91', '1'];
      const matchedCode = possibleCodes.find((code) => cleaned.startsWith(code));
      if (matchedCode) {
        cCode = matchedCode;
        phoneNumber = cleaned.slice(matchedCode.length);
      }
    } else {
      const possibleCodes = ['966', '971', '91', '1'];
      const matchedCode = possibleCodes.find((code) => phoneNumber.startsWith(code));
      if (matchedCode) {
        cCode = matchedCode;
        phoneNumber = phoneNumber.slice(matchedCode.length);
      }
    }

    setEditData({
      id: chargingStationData?._id, // VERY IMPORTANT
      name: chargingStationData?.name || '',
      address: chargingStationData?.address || '',
      electricityContractType: chargingStationData?.electricityContractType || '',
      StationLocation: chargingStationData?.stationLocation || '',
      accountCustomId: chargingStationData?.accountCustomId || '',
      latitude: chargingStationData?.latitude || '',
      longitude: chargingStationData?.longitude || '',
      electricityCostByUtilityProvider:
        chargingStationData?.electricityCostByUtilityProvider &&
        chargingStationData.electricityCostByUtilityProvider > 0
          ? chargingStationData.electricityCostByUtilityProvider
          : '',
      isGuestChargingExclusive: chargingStationData?.isGuestChargingExclusive || false,
      emergencyContact: phoneNumber,
      countryCode: cCode,

      editLandOwnerShipDocument: [],
      editEbReport: [],
      editMedias: [],
      removedMedias: [],
      removedLandOwnerShipDocument: [],
      removedEbReport: [],
      amenities: chargingStationData?.amenities || [],
      geoLocations: chargingStationData?.geoLocations || [],
      timeZone: normalizeTimeZone(chargingStationData?.time_zone || getUserTimeZone()),
      operatingHours: chargingStationData?.operatingHours
        ? {
            allDay: [
              'monday',
              'tuesday',
              'wednesday',
              'thursday',
              'friday',
              'saturday',
              'sunday',
            ].every((dKey) => {
              const dObject = (chargingStationData.operatingHours as any)?.[dKey];
              return (
                dObject?.status === 'open' &&
                (dObject?.open || '00:00') === '00:00' &&
                (dObject?.close || '23:59') === '23:59'
              );
            }),
            monday: {
              isOpen: chargingStationData.operatingHours.monday?.status === 'open',
              from: chargingStationData.operatingHours.monday?.open || '00:00',
              to: chargingStationData.operatingHours.monday?.close || '23:59',
              is24Hours:
                (chargingStationData.operatingHours.monday?.open || '00:00') === '00:00' &&
                (chargingStationData.operatingHours.monday?.close || '23:59') === '23:59',
            },
            tuesday: {
              isOpen: chargingStationData.operatingHours.tuesday?.status === 'open',
              from: chargingStationData.operatingHours.tuesday?.open || '00:00',
              to: chargingStationData.operatingHours.tuesday?.close || '23:59',
              is24Hours:
                (chargingStationData.operatingHours.tuesday?.open || '00:00') === '00:00' &&
                (chargingStationData.operatingHours.tuesday?.close || '23:59') === '23:59',
            },
            wednesday: {
              isOpen: chargingStationData.operatingHours.wednesday?.status === 'open',
              from: chargingStationData.operatingHours.wednesday?.open || '00:00',
              to: chargingStationData.operatingHours.wednesday?.close || '23:59',
              is24Hours:
                (chargingStationData.operatingHours.wednesday?.open || '00:00') === '00:00' &&
                (chargingStationData.operatingHours.wednesday?.close || '23:59') === '23:59',
            },
            thursday: {
              isOpen: chargingStationData.operatingHours.thursday?.status === 'open',
              from: chargingStationData.operatingHours.thursday?.open || '00:00',
              to: chargingStationData.operatingHours.thursday?.close || '23:59',
              is24Hours:
                (chargingStationData.operatingHours.thursday?.open || '00:00') === '00:00' &&
                (chargingStationData.operatingHours.thursday?.close || '23:59') === '23:59',
            },
            friday: {
              isOpen: chargingStationData.operatingHours.friday?.status === 'open',
              from: chargingStationData.operatingHours.friday?.open || '00:00',
              to: chargingStationData.operatingHours.friday?.close || '23:59',
              is24Hours:
                (chargingStationData.operatingHours.friday?.open || '00:00') === '00:00' &&
                (chargingStationData.operatingHours.friday?.close || '23:59') === '23:59',
            },
            saturday: {
              isOpen: chargingStationData.operatingHours.saturday?.status === 'open',
              from: chargingStationData.operatingHours.saturday?.open || '00:00',
              to: chargingStationData.operatingHours.saturday?.close || '23:59',
              is24Hours:
                (chargingStationData.operatingHours.saturday?.open || '00:00') === '00:00' &&
                (chargingStationData.operatingHours.saturday?.close || '23:59') === '23:59',
            },
            sunday: {
              isOpen: chargingStationData.operatingHours.sunday?.status === 'open',
              from: chargingStationData.operatingHours.sunday?.open || '00:00',
              to: chargingStationData.operatingHours.sunday?.close || '23:59',
              is24Hours:
                (chargingStationData.operatingHours.sunday?.open || '00:00') === '00:00' &&
                (chargingStationData.operatingHours.sunday?.close || '23:59') === '23:59',
            },
          }
        : {
            allDay: true,
            monday: { isOpen: true, from: '00:00', to: '23:59', is24Hours: true },
            tuesday: { isOpen: true, from: '00:00', to: '23:59', is24Hours: true },
            wednesday: { isOpen: true, from: '00:00', to: '23:59', is24Hours: true },
            thursday: { isOpen: true, from: '00:00', to: '23:59', is24Hours: true },
            friday: { isOpen: true, from: '00:00', to: '23:59', is24Hours: true },
            saturday: { isOpen: true, from: '00:00', to: '23:59', is24Hours: true },
            sunday: { isOpen: true, from: '00:00', to: '23:59', is24Hours: true },
          },
    });

    setNavigationDirections(
      (chargingStationData as any)?.navigationDirections?.length
        ? (chargingStationData as any).navigationDirections.map((item: any) => ({
            language: item.language,
            direction: item.direction || item.directions || '',
          }))
        : [{ language: '', direction: '' }],
    );
    setAdditionalDocuments([]);
  };

  /**
   * Toggle edit mode
   */
  const toggleEditMode = () => {
    if (isEdit) {
      setIsEdit(false);
      setEditData({});
      onEditModeChange?.(false);
      return;
    }

    handlePopulateEditData();
    setIsEdit(true);
    onEditModeChange?.(true);
  };

  /**
   * Handle remove existing file
   * @param {any} name - File name
   * @param {any} url - File url
   * @param {any} index - File index
   * @param {any} type - File type
   */
  const handleRemoveExistingFile = (
    name: string,
    url: string,
    index: number,
    type: 'newFile' | 'existingFile',
  ) => {
    if (type === 'newFile') {
      setEditData((previous: any) => ({
        ...previous,
        [name]: (previous[name] || []).filter((_: any, index_: number) => index_ !== index),
      }));
    } else if (type === 'existingFile') {
      if (name === 'landOwnerShipDocument') {
        setEditData((previous: any) => ({
          ...previous,
          removedLandOwnerShipDocument: [...(previous.removedLandOwnerShipDocument || []), url],
        }));
        setChargingStationData((previous: any) => ({
          ...previous,
          landOwnerShipDocument: (previous?.landOwnerShipDocument || []).filter(
            (u: any) => u?.url !== url,
          ),
        }));
      } else if (name === 'ebReport') {
        setEditData((previous: any) => ({
          ...previous,
          removedEbReport: [...(previous.removedEbReport || []), url],
        }));
        setChargingStationData((previous: any) => ({
          ...previous,
          ebReport: (previous?.ebReport || []).filter((u: any) => u?.url !== url),
        }));
      }
    }
  };

  /**
   * Navigate back
   * @param {any} fetchUpdatedData - The fetchUpdatedData
   */
  const handleNavigateBack = (fetchUpdatedData: boolean = false) => {
    setEditData({
      id: '',
      _id: '',
      name: '',
      landOwnerShipDocument: null,
      editLandOwnerShipDocument: [],
      removedLandOwnerShipDocument: [],
      ebReport: null,
      editEbReport: [],
      removedEbReport: [],
      electricityContractType: '',
      address: '',
      latitude: null,
      longitude: null,
      medias: null,
      editMedias: [],
      removedMedias: [],
      amenities: [],
      electricityCostByUtilityProvider: null,
      isGuestChargingExclusive: false,
      geoLocations: [],
    });
    setModalBoxStatus('close');
    setNavigationDirections([{ language: '', direction: '' }]);
    setAdditionalDocuments([]);

    if (fetchIdFrom === 'props' || onEditModeChange) {
      onEditModeChange?.(false);
      if (fetchUpdatedData) {
        onSuccess?.();
      }
      return;
    }

    if (status) {
      navigate(`../${status}`);
    } else {
      navigate(-1);
    }

    if (status === 'approved' && fetchUpdatedData) {
      dispatch(fetchApprovedStations({ page: approvedPage, limit: approvedLimit }));
    } else if (status === 'rejected' && fetchUpdatedData) {
      dispatch(fetchRejectedStations({ page: rejectedPage, limit: rejectedLimit }));
    } else if (status === 'pending' && fetchUpdatedData) {
      dispatch(fetchPendingStations({ page: approvedPage, limit: approvedLimit }));
    }
  };

  /**
   * Function to get and handle file upload progress
   * @param {any} progress - The progress
   */
  const handleUploadProgress = (progress: NewProgressProperties) => {
    if (progress?.fileName && progress?.percentage) {
      dispatch(updateFileUploadProgress(progress));
    }
  };

  /**
   * Update station API call
   */
  const handleSubmitStationDetails = () => {
    if (
      (editData.editLandOwnerShipDocument?.length > 0 &&
        !checkFileSizeValidation(editData.editLandOwnerShipDocument, 1, 1)) ||
      (editData.editEbReport?.length > 0 && !checkFileSizeValidation(editData.editEbReport, 1, 1))
    ) {
      return;
    }
    const hasInvalidElectricityCost =
      editData.electricityCostByUtilityProvider !== null &&
      editData.electricityCostByUtilityProvider !== undefined &&
      editData.electricityCostByUtilityProvider !== '' &&
      Number(editData.electricityCostByUtilityProvider) !== 0 &&
      Number(editData.electricityCostByUtilityProvider) < 0.1;

    if (
      stringValidation(1, 100, editData.name) &&
      longTextValidation(500, editData.address) &&
      editData.latitude &&
      editData.longitude &&
      !hasInvalidElectricityCost
    ) {
      setFormStatus({
        buttonLoader: true,
      });
      const formData = new FormData();

      // Append simple fields
      formData.append('name', editData.name);
      formData.append('accountCustomId', editData.accountCustomId);
      if (editData.electricityContractType) {
        formData.append('electricityContractType', editData.electricityContractType);
      }
      if (editData.countryCode && editData.emergencyContact) {
        formData.append('countryCode', editData.countryCode);
        formData.append('emergencyContact', editData.emergencyContact);
      }
      formData.append('address', editData.address);
      if (
        editData.electricityCostByUtilityProvider !== null &&
        editData.electricityCostByUtilityProvider !== undefined &&
        String(editData.electricityCostByUtilityProvider) !== ''
      ) {
        formData.append(
          'electricityCostByUtilityProvider',
          editData.electricityCostByUtilityProvider.toString(),
        );
      }
      formData.append('isGuestChargingExclusive', 'false');
      formData.append('latitude', editData.latitude ? editData.latitude.toString() : '');
      formData.append('longitude', editData.longitude ? editData.longitude.toString() : '');
      // Append operatingHours and timezone
      if (editData.operatingHours) {
        /** operating hours */
        const days = [
          'monday',
          'tuesday',
          'wednesday',
          'thursday',
          'friday',
          'saturday',
          'sunday',
        ] as const;

        days.forEach((day) => {
          const dayData = editData.operatingHours?.[day];

          if (dayData?.isOpen) {
            formData.append(`operatingHours[${day}][open]`, dayData.from);
            formData.append(`operatingHours[${day}][close]`, dayData.to);
            formData.append(`operatingHours[${day}][status]`, 'open');
          } else {
            formData.append(`operatingHours[${day}][status]`, 'closed');
          }
        });
      }

      if (editData.timeZone) {
        formData.append('time_zone', editData.timeZone || '');
      }

      // Append files for landOwnerShipDocument if it's an array
      if (Array.isArray(editData.editLandOwnerShipDocument) && status !== 'approved') {
        editData.editLandOwnerShipDocument.forEach((file: any) => {
          formData.append(`landOwnerShipDocument`, file);
        });
      }

      // Append files for ebReport if it's an array
      if (Array.isArray(editData.editEbReport) && status !== 'approved') {
        editData.editEbReport.forEach((file: any) => {
          formData.append(`ebReport`, file);
        });
      }

      // Append multiple files for medias (if it's an array)
      if (editData.editMedias && Array.isArray(editData.editMedias)) {
        editData.editMedias.forEach((media: any) => {
          formData.append(`medias`, media);
        });
      }

      // Append multiple amenities
      if (editData.amenities && Array.isArray(editData.amenities)) {
        editData.amenities.forEach((amenity: string, index: number) => {
          formData.append(`amenities[${index}]`, amenity);
        });
      }

      const languageCountryMap: Record<string, string> = {
        Arabic: 'Saudi Arabia',
        English: 'United States',
        French: 'France',
        Spanish: 'Spain',
      };

      const activeNavigationDirections = navigationDirections.filter((item: any) => item.language);
      activeNavigationDirections.forEach((nav: any, index: number) => {
        const country = languageCountryMap[nav.language] || '';
        formData.append(`navigationDirections[${index}][country]`, country);
        formData.append(`navigationDirections[${index}][language]`, nav.language);
        formData.append(`navigationDirections[${index}][direction]`, nav.direction);
      });

      additionalDocuments.forEach((filesList) => {
        if (Array.isArray(filesList)) {
          filesList.forEach((file) => {
            formData.append(`additionalDocuments`, file);
          });
        }
      });

      // RemovedURLs
      let removedURL: string[] = [...(editData.removedMedias || [])];

      if (status !== 'approved' && editData.removedEbReport) {
        removedURL = [...removedURL, ...editData.removedEbReport];
      } else if (status === 'approved' && editData.removedEbReport.length > 0) {
        const error: any = {
          response: {
            data: {
              message: t('ebReportEditNotAllowed'),
            },
          },
        };

        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || 'Something went wrong!',
            type: 'error',
            code: 0,
          }),
        );

        setFormStatus({
          buttonLoader: false,
        });

        return;
      }

      if (status !== 'approved' && editData.removedLandOwnerShipDocument) {
        removedURL = [...removedURL, ...editData.removedLandOwnerShipDocument];
      } else if (status === 'approved' && editData.removedLandOwnerShipDocument.length > 0) {
        const error: any = {
          response: {
            data: {
              message: t('landDocEditNotAllowed'),
            },
          },
        };

        dispatch(
          setNotificationMessage({
            status: true,
            message: error?.response?.data?.message || 'Something went wrong!',
            type: 'error',
            code: 0,
          }),
        );

        setFormStatus({
          buttonLoader: false,
        });

        return;
      }

      if (removedURL.length > 0) {
        if (removedURL?.length) {
          removedURL.forEach((url, index) => {
            formData.append(`removedUrls[${index}]`, url);
          });
        }
      }

      const binaryFileData: SingleFileUploadProgress[] = [
        ...(editData.editLandOwnerShipDocument || []),
        ...(editData.editEbReport || []),
        ...(editData.editMedias || []),
        ...additionalDocuments.flat(),
      ]?.map((fileData) => ({
        fileName: SanitizeFileName(fileData.name),
        progress: 0,
        status: 'pending',
      }));

      if (binaryFileData?.length) {
        dispatch(setUnUploadedFileData(binaryFileData));
        setModalBoxStatus('open');
        setModalBoxDetail({
          title:
            binaryFileData?.length === 1
              ? t('uploadingFile')
              : `${binaryFileData?.length} ${t('uploadingFiles')}`,
          content: <FileUploadProgress />,
          notificationModal: false,
        });
      }

      if (editData.geoLocations) {
        editData.geoLocations.forEach((geo: any, index: number) => {
          formData.append(`geoLocations[${index}][name]`, geo.name);
          formData.append(`geoLocations[${index}][key]`, geo.key);
          formData.append(`geoLocations[${index}][value]`, geo.value);
        });
      }

      chargingStation
        .editStation(editData.id, formData, handleUploadProgress)
        .then(() => {
          setModalBoxStatus('open');
          setFormStatus({
            buttonLoader: false,
          });
          setModalBoxDetail({
            title: null,
            content: (
              <SuccessNotification
                title={t('stationEditedSuccessfully')}
                description={t('stationEditedDescription')}
                notificationCloseTimer={3000}
                onNotificationClose={() => handleNavigateBack(true)}
              />
            ),
            notificationModal: true,
          });
        })
        .catch((error) => {
          setModalBoxStatus('close');
          setFormStatus({
            buttonLoader: false,
          });

          const isNetworkError =
            error?.message === 'Failed to fetch' ||
            error?.message?.includes('fetch') ||
            error?.message === 'Network Error';
          const totalNewFiles =
            (editData.editLandOwnerShipDocument?.length || 0) +
            (editData.editEbReport?.length || 0) +
            (editData.editMedias?.length || 0) +
            (additionalDocuments.flat()?.length || 0);

          let errorMessage = '';
          if (error?.response?.status === 413 || (isNetworkError && totalNewFiles > 0)) {
            errorMessage = t(
              'requestEntityTooLarge',
              'Request Entity Too Large: The uploaded files exceed the allowed limit.',
            );
          } else {
            errorMessage = error?.response?.data?.message || 'Something went wrong!';
          }

          const isDuplicateLatLong =
            errorMessage.includes(
              'A charging station already exists with the same latitude and longitude',
            ) ||
            errorMessage
              .toLowerCase()
              .includes('already exists with the same latitude and longitude') ||
            errorMessage.toLowerCase().includes('duplicate latitude and longitude') ||
            errorMessage.toLowerCase().includes('latitude and longitude already exists');

          setLatLongError(isDuplicateLatLong ? errorMessage : '');

          dispatch(
            setNotificationMessage({
              status: true,
              message: errorMessage,
              type: 'error',
              code: error?.response?.status || 0,
            }),
          );
        });
    } else {
      const error: any = {
        response: {
          data: {
            message: t('fillRequiredFields'),
          },
        },
      };

      dispatch(
        setNotificationMessage({
          status: true,
          message: error?.response?.data?.message || 'Something went wrong!',
          type: 'error',
          code: error?.response?.status || 0,
        }),
      );
    }
  };
  // Selectors for different states
  const unapprovedState = useAppSelector((state) => state.partnersPendingStation);

  /**
   * Refresh data function
   */
  const refreshData = () => {
    if (unapprovedState) {
      const { filters, limit, page: approvedPageList, sortBy, sortField } = unapprovedState;
      const queryParameters = GetParametersFromCommonFilters(filters);
      dispatch(
        fetchPendingStations({ ...queryParameters, limit, approvedPageList, sortBy, sortField }),
      );
    }
  };
  /**
   * Handle approve action
   */
  const handleApprove = async () => {
    if (!chargingStationData?._id) return;
    setActionLoading(true);
    try {
      await chargingStation.statusChangeChargingStation(chargingStationData._id, {
        submissionStatus: 'approved',
      });
      setActionLoading(false);
      setModalBoxStatus('open');
      setModalBoxDetail({
        title: null,
        content: (
          <SuccessNotification
            title="Station Approved Successfully"
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setModalBoxStatus('close');
              refreshData();
              const parameters = new URLSearchParams(searchParameters);
              parameters.delete('stationId');
              setSearchParameters(parameters);
              dispatch(fetchPendingNotification());
              dispatch(fetchApprovedStations({ page: approvedPage, limit: approvedLimit }));
              dispatch(fetchPendingStations({ page: approvedPage, limit: approvedLimit }));
            }}
          />
        ),
        notificationModal: true,
      });
    } catch (error: any) {
      setActionLoading(false);
      dispatch(
        setNotificationMessage({
          status: true,
          message: error?.response?.data?.message || t('errorMessage'),
          type: 'error',
          code: 0,
        }),
      );
    }
  };

  /**
   * Handle reject click
   */
  const handleRejectClick = () => {
    setLocalUsageCostView('rejectForm');
  };

  /**
   * Handle cancel reject
   */
  const handleCancelReject = () => {
    setLocalUsageCostView('pending');
    setRejectReason('');
  };

  /**
   * Handle proceed with reject
   */
  const handleProceedReject = async () => {
    if (!chargingStationData?._id) return;
    try {
      await chargingStation.statusChangeChargingStation(chargingStationData._id, {
        submissionStatus: 'rejected',
        reason: rejectReason,
      });
      setModalBoxStatus('open');
      setModalBoxDetail({
        title: null,
        content: (
          <SuccessNotification
            title="Station Rejected Successfully"
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setModalBoxStatus('close');
              refreshData();
              const parameters = new URLSearchParams(searchParameters);
              parameters.delete('stationId');
              setSearchParameters(parameters);
              dispatch(fetchPendingNotification());
              dispatch(fetchRejectedStations({ page: rejectedPage, limit: rejectedLimit }));
              dispatch(fetchPendingStations({ page: approvedPage, limit: approvedLimit }));
            }}
          />
        ),
        notificationModal: true,
      });
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          status: true,
          message: error?.response?.data?.message || 'Something went wrong!',
          type: 'error',
          code: 0,
        }),
      );
    }
  };

  /**
   * Fetch account name list API and set the account name list state with the response data.
   */
  const handleAccountNameList = async () => {
    try {
      const response = await deviceApi.fetchAccountNameList();
      setAccountNameList(
        response.data.map((account: AddDeviceProperties) => ({
          label: account?.accountName,
          value: account?.accountCustomId,
        })),
      );
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };
  /**
   * handle Open Add Partner
   */
  const handleOpenAddPartner = () => {
    setModalBoxDetail({
      title: '',
      content: (
        <AddFacilityAccount
          closeModal={(message: any) => {
            setModalBoxStatus('close');
            if (message) {
              handleAccountNameList();
              dispatch(
                setNotificationMessage({
                  message,
                  status: true,
                  type: 'success',
                }),
              );
            }
          }}
          sidebarClosed={() => setModalBoxStatus('close')}
        />
      ),
      notificationModal: false,
    });
    setModalBoxStatus('open');
  };

  /**
   * Handling information icon click
   * @param {any} customId - The custom account ID
   */
  const handleOpenFacilityInformation = (customId?: string | number) => {
    if (customId) {
      navigate(`?accountCustomId=${customId.toString()}`, {
        state: { fromStationId: searchParameters.get('stationId') },
      });
    }
  };

  useEffect(() => {
    let stationId;
    if (fetchIdFrom === 'queryParams') {
      stationId = searchParameters.get('stationId');
    } else if (fetchIdFrom === 'props') {
      stationId = id;
    }

    if (!stationId) {
      return;
    }

    handleFetchStationDetails(stationId);
    handleFetchDeviceDetails(stationId);
  }, [searchParameters, id]);

  useEffect(() => {
    handleAccountNameList();
  }, []);
  useEffect(() => {
    if (initialIsEditMode) {
      if (chargingStationData?._id) {
        handlePopulateEditData();
        setIsEdit(true);
        setOpenTab(0);
        onEditModeChange?.(true);
      }
    } else {
      setIsEdit(false);
      setOpenTab(0);
      onEditModeChange?.(false);
    }
  }, [chargingStationData?._id, initialIsEditMode]);

  if (isLoader) {
    return (
      <div className="loader-wrap">
        <div className="loader">
          <BatteryLoader />
        </div>
      </div>
    );
  }

  return (
    <div className="station-side-panel-wrap" dir={i18n.dir()}>
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        header={false}
      />
      <ModalBox
        open={locationPopup === 'open'}
        title={t('address')}
        content={
          <SelectLatLong
            latitude={chargingStationData?.latitude || null}
            longitude={chargingStationData?.longitude || null}
            handleLatLongChange={() => setLocationPopup('close')}
          />
        }
        closeModal={() => setLocationPopup('close')}
        header
        modalBoxAnimation="modalAnimateToLeft"
      />
      <ModalBox
        title={selectedDocument?.name || t('documentPreview')}
        open={isDocumentModalOpen}
        closeModal={handleCloseDocumentModal}
        header
        position="center"
        content={
          <div className="document-preview-container">
            {(() => {
              if (!selectedDocument?.url)
                return <p className="document-preview-text">{t('noDocumentToDisplay')}</p>;

              if (selectedDocument.type.includes('image')) {
                return (
                  <img
                    src={selectedDocument.url}
                    alt="Document"
                    className="document-preview-image"
                  />
                );
              }

              return (
                <iframe
                  src={selectedDocument.url}
                  title="Document Viewer"
                  className="document-preview-iframe"
                />
              );
            })()}
          </div>
        }
      />
      {fetchIdFrom === 'props' && (
        <div className="side-panel-header-component">
          <div className={`side-panel-header-left-title-actions ${isEdit ? 'no-border' : ''}`}>
            <p className="side-panel-header-title">
              {isEdit ? t('editStationDetails') : t('stationDetails')}
            </p>
            <div className="side-panel-header-left-icons">
              {!isEdit && (
                <>
                  {/* Expand Pages */}
                  <div className="button-wrap">
                    <span
                      role="presentation"
                      className="access-facility"
                      onClick={() => expandFunction('new')}>
                      <ExpandIcon className="icon" />
                    </span>
                    <div className="hover-label">{t('expandPages', 'Expand Pages')}</div>
                  </div>

                  {/* Edit Icon */}
                  {(permissionCheck('partnerChargingStationUpdate') ||
                    permissionCheck('cpoChargingStationUpdate')) && (
                    <div className="button-wrap">
                      <span
                        role="presentation"
                        className="access-facility"
                        onClick={toggleEditMode}>
                        <EditPencilCard className="icon" />
                      </span>
                      <div className="hover-label">{t('edit', 'Edit')}</div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <CloseIcon className="icon close-btn" onClick={onClose} />
        </div>
      )}
      {isEdit && (
        <div className="add-station-tabs">
          <TabToggle
            tabs={[t('stationInformation'), t('operatingHours')]}
            defaultTab="stationInformation"
            onChange={(tab) => changeTabPages(tab)}
          />
        </div>
      )}
      {/* ===================== STATION INFO (TOP) ===================== */}
      <div className="station-scrollable-content">
        {!isEdit && (
          <div className="station-data-wrapper">
            <div className="station-card-header">
              <div className="header-left-new">
                <img
                  className="station-header-image"
                  src={chargingStationData?.medias?.[0]?.signedUrl || DefaultStationImage}
                  alt="station"
                  onError={(parameter) => {
                    const imgElement = parameter.currentTarget;
                    imgElement.src = DefaultStationImage;
                  }}
                />
                <div className="header-text-new">
                  <h3 className="station-name-title">{chargingStationData?.name ?? 'N/A'}</h3>
                  <div className="station-id-row">
                    {portalType === PortalType.PARTNER ? (
                      <span className="station-id-text-plain">
                        {chargingStationData?.accountCustomId ?? 'N/A'}
                      </span>
                    ) : (
                      <span
                        className="station-id-text"
                        onClick={() =>
                          handleOpenFacilityInformation(chargingStationData?.accountCustomId)
                        }
                        role="presentation">
                        {chargingStationData?.accountCustomId ?? 'N/A'}
                      </span>
                    )}
                  </div>
                  <div className="station-status-badge-row">{renderStatusBadge()}</div>
                </div>
              </div>
              <div className="header-right">
                <div className="header-actions">
                  {chargingStationData?.submission?.status === 'approved' && (
                    <MyButton
                      label={t('accessStation')}
                      buttonType="primary"
                      onClickFunc={() => expandFunction('new')}
                      labelColorFill="#fff"
                      svgBackIcon={<ArrowRight />}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="tab-toggle-container">
              <TabToggle
                tabs={[
                  { label: t('basicInfo', 'Basic info'), value: 'details' },
                  { label: t('associatedChargers', 'Associated chargers'), value: 'chargers' },
                  { label: t('Media & Amenities', 'Media & Amenities'), value: 'mediaAmenities' },
                ]}
                defaultTab="details"
                onChange={(value) => setActiveTab(value)}
                isLabelValue
              />
            </div>
            {activeTab === 'details' && (
              <div className="station-content-wrap">
                {chargingStationData &&
                  (PortalType.NETWORKS === portalType ||
                    PortalType.APP === portalType ||
                    PortalType.NETWORKS === portalType) &&
                  (chargingStationData as any)?.submission?.status === 'pending' && (
                    <>
                      <div className="section-block usage-cost-block">
                        <h4>{t('stationVerification')}</h4>

                        <div className="usage-cost-card">
                          {/* Action Buttons */}
                          {localUsageCostView === 'pending' && (
                            <div className="button-row">
                              {actionLoading && (
                                <div
                                  style={{
                                    position: 'fixed',
                                    top: 0,
                                    left: 0,
                                    width: '100vw',
                                    height: '100vh',
                                    zIndex: 99999,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                  }}>
                                  <BatteryLoader />
                                </div>
                              )}
                              <MyButton
                                label={t('reject')}
                                buttonType="danger-outline"
                                onClickFunc={handleRejectClick}
                                disableButton={
                                  !permissionCheck('cpoChargingStationReject') || actionLoading
                                }
                              />

                              <MyButton
                                label={t('approve')}
                                buttonType="primary"
                                onClickFunc={handleApprove}
                                disableHoverEffect={false}
                                disableButton={
                                  !permissionCheck('cpoChargingStationApprove') || actionLoading
                                }
                              />
                            </div>
                          )}

                          {/* Reject Form */}
                          {localUsageCostView === 'rejectForm' && (
                            <div className="reject-form-container">
                              <textarea
                                className="reject-reason-input"
                                placeholder={t('reasonForReject')}
                                value={rejectReason}
                                onChange={(event) => setRejectReason(event.target.value)}
                              />

                              <div className="button-row">
                                {actionLoading && (
                                  <div
                                    style={{
                                      position: 'fixed',
                                      top: 0,
                                      left: 0,
                                      width: '100vw',
                                      height: '100vh',
                                      zIndex: 99999,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    }}>
                                    <BatteryLoader />
                                  </div>
                                )}
                                <MyButton
                                  label={t('cancel')}
                                  buttonType="outline"
                                  onClickFunc={handleCancelReject}
                                  disableButton={actionLoading}
                                />

                                <MyButton
                                  label={t('reject')}
                                  buttonType="error"
                                  onClickFunc={handleProceedReject}
                                  disableButton={actionLoading || !rejectReason.trim()}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="divider" />
                    </>
                  )}
                {/* {chargingStationData?.submission?.status === 'rejected' &&
                  (PortalType.CPO === portalType || PortalType.APP === portalType) && (
                    <>
                      <div className="rejection-reason-container">
                        <span className="label">{t('rejectionReason')}:</span>
                        <p className="value">
                          {(chargingStationData as any)?.submission?.reason ||
                            (chargingStationData as any)?.reason ||
                            '-'}
                        </p>
                      </div>
                      <div className="divider" />
                    </>
                  )} */}
                <div className="station-basic-details">
                  {status === 'rejected' && !isEdit && (
                    <div className="station-status">
                      <p className="title">{t('stationVerification')}</p>
                      <div className="reason">
                        <p className="reason-text">
                          {t('rejectedReason')}:{' '}
                          <span>{chargingStationData?.submission?.reason || 'N/A'}</span>
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="station-details-section">
                    <h4 className="section-title-large">
                      {t('generalInformation', 'General Information')}
                    </h4>

                    <div className="detail-row">
                      <div className="detail-label-wrap">
                        <PhoneIcon className="detail-icon" />
                        <span className="detail-label">
                          {t('emergencyContact', 'Emergency Contact')}
                        </span>
                      </div>
                      <span className="detail-value">
                        {chargingStationData?.countryCode && chargingStationData?.emergencyContact
                          ? `+${chargingStationData.countryCode} ${chargingStationData.emergencyContact}`
                          : chargingStationData?.emergencyContact || 'N/A'}
                      </span>
                    </div>

                    <div className="detail-row">
                      <div className="detail-label-wrap">
                        <GlobeIcon className="detail-icon" />
                        <span className="detail-label">{t('timeZone', 'Time Zone')}</span>
                      </div>
                      <span className="detail-value">
                        {chargingStationData?.time_zone || 'N/A'}
                      </span>
                    </div>

                    <div className="detail-row alignment-top">
                      <div className="detail-label-wrap">
                        <ClockIcon className="detail-icon" />
                        <span className="detail-label">
                          {t('operatingHours', 'Operating Hours')}
                        </span>
                      </div>
                    </div>

                    {chargingStationData?.operatingHours && (
                      <div className="operating-hours-card">
                        {[
                          { key: 'monday', label: t('monday', 'Monday') },
                          { key: 'tuesday', label: t('tuesday', 'Tuesday') },
                          { key: 'wednesday', label: t('wednesday', 'Wednesday') },
                          { key: 'thursday', label: t('thursday', 'Thursday') },
                          { key: 'friday', label: t('friday', 'Friday') },
                          { key: 'saturday', label: t('saturday', 'Saturday') },
                          { key: 'sunday', label: t('sunday', 'Sunday') },
                        ].map((day) => {
                          const dayData = (chargingStationData.operatingHours as any)?.[day.key];
                          let timeString = t('closed', 'Closed');
                          if (dayData?.status === 'open') {
                            if (dayData.open === '00:00' && dayData.close === '23:59') {
                              timeString = t('open24Hours', 'Open 24 hours');
                            } else {
                              timeString = `${convertTo12HourFormat(dayData.open)} - ${convertTo12HourFormat(dayData.close)}`;
                            }
                          }
                          return (
                            <div key={day.key} className="day-schedule-row">
                              <span className="day-name">{day.label}</span>
                              <span
                                className={`day-time ${dayData?.status === 'open' ? 'open' : 'closed'}`}>
                                {timeString}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div className="detail-row alignment-top">
                      <div className="detail-label-wrap">
                        <LocationIcon className="detail-icon" />
                        <span className="detail-label">{t('addressLabel', 'Address')}</span>
                      </div>
                      <span
                        className="detail-value clickable-address"
                        onClick={() => setLocationPopup('open')}
                        role="presentation">
                        {chargingStationData?.address || 'N/A'}
                      </span>
                    </div>

                    <div className="detail-row">
                      <div className="detail-label-wrap">
                        <CalendarIcon className="detail-icon" />
                        <span className="detail-label">{t('Created', 'Created')}</span>
                      </div>
                      <span className="detail-value">
                        {chargingStationData?.createdAt
                          ? ConvertUTCtoDateTime(chargingStationData.createdAt)
                          : 'N/A'}
                      </span>
                    </div>

                    <div className="detail-row">
                      <div className="detail-label-wrap">
                        <CalendarV2Icon className="detail-icon" />
                        <span className="detail-label">{t('Last Updated', 'Last Updated')}</span>
                      </div>
                      <span className="detail-value">
                        {chargingStationData?.updatedAt
                          ? ConvertUTCtoDateTime(chargingStationData.updatedAt)
                          : 'N/A'}
                      </span>
                    </div>

                    <div className="detail-row">
                      <div className="detail-label-wrap">
                        <EyeIcon className="detail-icon" />
                        <span className="detail-label">
                          {t('publiclyVisible', 'Publicly Visible')}
                        </span>
                      </div>
                      <span className="detail-value status-value">
                        {chargingStationData?.isAvailable ? (
                          <>
                            <CheckCircleIcon
                              className="status-success-icon"
                              style={{ width: 16, height: 16, color: '#4caf50' }}
                            />
                            <span>{t('yes', 'Yes')}</span>
                          </>
                        ) : (
                          t('no', 'No')
                        )}
                      </span>
                    </div>

                    {chargingStationData?.electricityContractType && (
                      <div className="detail-row">
                        <div className="detail-label-wrap">
                          <LightningIcon className="detail-icon" />
                          <span className="detail-label">
                            {t('electricityType', 'Electricity Type')}
                          </span>
                        </div>
                        <span className="detail-value contract-type-value">
                          <LightningIcon
                            className="contract-icon"
                            style={{ width: 14, height: 14, color: '#ff9800' }}
                          />
                          <span>
                            {chargingStationData.electricityContractType.charAt(0).toUpperCase() +
                              chargingStationData.electricityContractType.slice(1)}
                          </span>
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="station-details-section">
                    <h4 className="section-title-large">{t('mapLocation', 'Map Location')}</h4>

                    <div className="detail-row">
                      <div className="detail-label-wrap">
                        <LocationSelector className="detail-icon" />
                        <span className="detail-label">{t('latitude', 'Latitude')}</span>
                      </div>
                      <span className="detail-value font-numeric">
                        {chargingStationData?.latitude !== null &&
                        chargingStationData?.latitude !== undefined
                          ? chargingStationData.latitude
                          : 'N/A'}
                      </span>
                    </div>

                    <div className="detail-row">
                      <div className="detail-label-wrap">
                        <LocationSelector className="detail-icon" />
                        <span className="detail-label">{t('longitude', 'Longitude')}</span>
                      </div>
                      <span className="detail-value font-numeric">
                        {chargingStationData?.longitude !== null &&
                        chargingStationData?.longitude !== undefined
                          ? chargingStationData.longitude
                          : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
                {chargingStationData?.landOwnerShipDocument?.length ||
                chargingStationData?.ebReport?.length ? (
                  <>
                    <div className="divider" />
                    <div className="station-basic-details">
                      <p className="title">{t('documents')}</p>

                      <div className="documents-row">
                        <DocumentChip
                          label={t('landDocument')}
                          file={chargingStationData?.landOwnerShipDocument?.[0]}
                          onClick={() =>
                            handleViewDocument(chargingStationData?.landOwnerShipDocument?.[0])
                          }
                        />
                        <DocumentChip
                          label={t('ebReports')}
                          file={chargingStationData?.ebReport?.[0]}
                          onClick={() => handleViewDocument(chargingStationData?.ebReport?.[0])}
                        />
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            )}
            {/* ===================== DEVICE INFO (BOTTOM) ===================== */}
            {activeTab === 'chargers' && !isEdit && allowList && (
              <div className="table-containers">
                <p className="heading">
                  {chargingPointData?.length > 1
                    ? t('associatedChargers', 'Associated Chargers')
                    : t('associatedCharger', 'Associated Charger')}
                </p>
                <div>
                  {' '}
                  <CustomTable
                    serialNo
                    header={column}
                    data={chargingPointData}
                    loading={false}
                    filterAllow={false}
                    sizeChangeButtonRequired={false}
                    limit={0}
                    totalPages={0}
                    totalResults={0}
                    height={620}
                    rowClickEvent={handleOpenDeviceSidePanel}
                    rowClickParamsKey="deviceId"
                    leftTopContent={
                      <p className="heading">
                        {chargingPointData?.length > 1
                          ? t('associatedChargers', 'Associated Chargers')
                          : t('associatedCharger', 'Associated Charger')}
                      </p>
                    }
                  />
                </div>
              </div>
            )}
            {/* Media */}
            {activeTab === 'mediaAmenities' && (
              <div className="section-block media-block">
                <p className="title">{t('mediaAmenities')}</p>

                {/* VIEW MODE MEDIA */}
                <div className="media-grid-view">
                  {chargingStationData?.medias && chargingStationData.medias.length > 0 ? (
                    chargingStationData?.medias?.slice(0, 4).map((media, index) => {
                      const remaining = (chargingStationData?.medias?.length || 0) - 4;
                      return (
                        <FilePreview
                          key={media.url}
                          file={media}
                          extraCount={index === 3 ? remaining : 0}
                          onClick={() => handleViewDocument(media)}
                        />
                      );
                    })
                  ) : (
                    <FilePreview
                      file={{
                        fileName: 'default-station.png',
                        fileSize: 0,
                        fileType: 'image/png',
                        url: DefaultStationImage,
                        signedUrl: DefaultStationImage,
                      }}
                      onClick={() => {}}
                    />
                  )}
                </div>

                {/* AMENITIES VIEW */}
                {chargingStationData?.amenities?.length ? (
                  <>
                    <div className="divider" />
                    <div className="amenities-container">
                      <p className="title">{t('amenities')}</p>

                      <div className="amenities-list">
                        {chargingStationData?.amenities?.map((each: any) => {
                          const amenityToShow =
                            amenitiesDetails.find((a) => a.key === each) || amenitiesDetails[0];

                          return (
                            <div className="single-amenity-item" key={each}>
                              <div className="icon-box">{amenityToShow.svgIconActive}</div>
                              <span>{amenityToShow.name}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            )}
          </div>
        )}

        {isEdit && (
          <div className="station-data-wrapper">
            {openTab === 0 && (
              <>
                <div className="station-content-wrap">
                  <div className="station-content-header">
                    {(PortalType.NETWORKS === portalType || PortalType.APP === portalType) && (
                      <div className="header-edit-row">
                        <p className="header-label">{t('accountName')}:</p>
                        <div className="header-input">
                          <SelectField
                            name="accountCustomId"
                            placeholder={t('selectAccountName')}
                            options={accountNameList}
                            showInformationIcon
                            onInformationClick={(options) =>
                              handleOpenFacilityInformation(options.value)
                            }
                            description={(option) => option.value}
                            selectedValue={editData.accountCustomId ?? ''}
                            onChangeValue={handleInputChange}
                            error={!editData.accountCustomId}
                            errorMessage={t('accountNameRequired')}
                            valueRequired
                            enableSearch
                            emptyActionLabel={t('station:addAccount') || 'Add Partner'}
                            onEmptyActionClick={handleOpenAddPartner}
                          />
                        </div>
                      </div>
                    )}

                    <div className="header-edit-row">
                      <p className="header-label">{t('stationName')}:</p>

                      <div className="header-input">
                        <CustomInput
                          type="text"
                          name="name"
                          placeholder={t('stationName')}
                          value={editData.name}
                          onChangeValue={handleInputChange}
                          valueRequired
                          error={!editData.name}
                          errorMessage={t('stationNameRequired')}
                        />
                      </div>
                    </div>
                    <div className="header-edit-row">
                      <p className="header-label">{t('emergencyContact', 'Emergency Contact')}:</p>
                      <div className="header-input">
                        <CustomPhoneInput
                          type="number"
                          name="emergencyContact"
                          placeholder={t('enterEmergencyContact', 'Enter Emergency Contact')}
                          value={editData.emergencyContact || ''}
                          value1={editData.countryCode}
                          onChangeValue={handleInputChange}
                          valueRequired={false}
                          minLength={5}
                          maxLength={13}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="divider" />
                  <div className="station-basic-details">
                    <p className="title">{t('basicInformation')}</p>

                    {/* Address */}
                    <div className="basic-info-item">
                      <p className="basic-info-label">
                        <span className="label-with-tooltip">{t('addressLabel')}</span>
                      </p>
                      <div className="basic-info-input">
                        <CustomAddressInput
                          placeholder={t('address')}
                          name="address"
                          value={editData.address}
                          onChangeValue={handleChangeAddress}
                          valueRequired
                          error={!editData.address?.trim() || !!latLongError}
                          errorMessage={latLongError || t('addressRequiredError')}
                        />
                      </div>
                    </div>
                    {/* Geo Location */}
                    {editData?.geoLocations?.map((location: any) => (
                      <div className="basic-info-item" key={location.key}>
                        <p className="basic-info-label">
                          <span className="label-with-tooltip">{location.name}</span>
                        </p>
                        <div className="basic-info-input">
                          <CustomInput
                            type="text"
                            name={location.key}
                            value={location.value}
                            onChangeValue={() => {}}
                            readOnly
                            placeholder=""
                          />
                        </div>
                      </div>
                    ))}

                    {/* Location */}
                    <div className="basic-info-item">
                      <p className="basic-info-label">{t('location')}:</p>
                      <SelectLocation
                        latitude={editData.latitude}
                        longitude={editData.longitude}
                        onChangeValue={handleChangeLatLong}
                        valueRequired
                        error={!(editData.latitude && editData.longitude) || !!latLongError}
                        errorMessage={latLongError || t('latLongRequiredError')}
                      />
                    </div>
                    {/* Electricity Contract Type */}
                    <div className="basic-info-item">
                      <p className="basic-info-label">
                        <span className="label-with-tooltip">{t('electricityType')}</span>
                      </p>
                      <div className="basic-info-input">
                        <SelectField
                          name="electricityContractType"
                          placeholder={t('chooseElectricityContractType')}
                          selectedValue={editData.electricityContractType}
                          onChangeValue={handleInputChange}
                          options={electricityContractType}
                          error={false}
                          errorMessage={t('electricityContractTypeRequired')}
                        />
                      </div>
                    </div>
                    {/* Electricity Cost (Optional) */}
                    <div className="basic-info-item">
                      <p className="basic-info-label">
                        <span className="label-with-tooltip">
                          {t('usageCost')}
                          {/* <TooltipIcon text={t('electricityCostTooltip')} /> */}
                        </span>
                      </p>
                      <div className="basic-info-input">
                        <CustomInput
                          type="number"
                          name="electricityCostByUtilityProvider"
                          placeholder={t('enterUsageCost') || 'Enter Electricity Cost'}
                          value={
                            Number(editData.electricityCostByUtilityProvider) === 0
                              ? ''
                              : editData.electricityCostByUtilityProvider
                          }
                          onChangeValue={(name, value) => {
                            setElectricityCostTouched(true);
                            handleInputChange(name, value);
                          }}
                          valueRequired={false}
                          error={
                            electricityCostTouched &&
                            editData.electricityCostByUtilityProvider !== null &&
                            editData.electricityCostByUtilityProvider !== undefined &&
                            editData.electricityCostByUtilityProvider !== '' &&
                            Number(editData.electricityCostByUtilityProvider) !== 0 &&
                            Number(editData.electricityCostByUtilityProvider) < 0.1
                          }
                          errorMessage={t('electricityCostMinError')}
                          maxValue={1000}
                          decimalLimit={3}
                        />
                      </div>
                    </div>

                    {/* Electricity Cost (Optional) - no guest charging */}
                  </div>
                  <div className="divider" />
                  {/* Navigation Directions */}
                  <div className="form-bottom navigation-directions-section">
                    <div className="navigation-directions-header">
                      <p className="title">{t('navigationDirections', 'Navigation Directions')}</p>
                      {navigationDirections.length < 4 && (
                        <button
                          type="button"
                          className="add-direction-btn"
                          onClick={handleAddNavigationDirection}>
                          + {t('add', 'Add')}
                        </button>
                      )}
                    </div>

                    <div className="navigation-directions-list">
                      {navigationDirections.map((item: any, index: number) => {
                        const availableOptions = getAvailableLanguageOptions(index);
                        return (
                          <div key={index} className="navigation-direction-row">
                            <div className="field-select">
                              <SelectField
                                inputLabel={t('language', 'Language')}
                                name={`language-${index}`}
                                placeholder={t('selectLanguage', 'Select Language')}
                                selectedValue={item.language}
                                onChangeValue={(_, value) => {
                                  const updated = [...navigationDirections];
                                  updated[index].language = value as string;
                                  updated[index].direction = '';
                                  setNavigationDirections(updated);
                                }}
                                options={availableOptions}
                                triggerOptions={getLanguageOptions()}
                              />
                            </div>
                            <div className="field-input">
                              <CustomInput
                                inputLabel={t('directions', 'Directions')}
                                type="text"
                                placeholder={
                                  item.language
                                    ? `${t('enterDirectionsIn', 'Enter directions in')} ${item.language}`
                                    : t('selectLanguageFirst', 'Select language first')
                                }
                                name={`directions-${index}`}
                                value={item.direction}
                                onChangeValue={(name, value) =>
                                  handleDirectionsChange(index, value as string)
                                }
                                disabled={!item.language}
                                hint={false}
                                error={!!item.language && !item.direction.trim()}
                                errorMessage={t('Directionisrequired')}
                              />
                            </div>
                            {navigationDirections.length > 1 && (
                              <button
                                type="button"
                                className="remove-direction-btn"
                                onClick={() => {
                                  setNavigationDirections(
                                    navigationDirections.filter(
                                      (_: any, index_: number) => index_ !== index,
                                    ),
                                  );
                                }}>
                                {t('remove', 'Remove')}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="section-block media-block">
                  <p className="title">
                    <span className="label-with-tooltip">
                      {t('mediasAndAmenities', 'Medias and Amenities')}
                    </span>
                  </p>
                  <CustomMediaUpload
                    inputLabel={t('Medias', 'Medias')}
                    name="editMedias"
                    selectedFiles={editData.editMedias}
                    existingName="medias"
                    existingFiles={
                      (!chargingStationData?.medias || chargingStationData.medias.length === 0) &&
                      (!editData.editMedias || editData.editMedias.length === 0)
                        ? [
                            {
                              fileName: 'default-station.png',
                              fileSize: 0,
                              fileType: 'image/png',
                              url: DefaultStationImage,
                              signedUrl: DefaultStationImage,
                            },
                          ]
                        : chargingStationData?.medias
                    }
                    onChangeValue={handleInputChange}
                    unselectHandler={handleRemoveMedias}
                    acceptedFileType="image/* video/*"
                    placeholder={t('uploadMedia')}
                    error={false}
                  />
                  <AmenitiesInput
                    selectedAmenities={editData.amenities}
                    handleStationDetailChange={(name: string, value: any) =>
                      setEditData((previous: any) => ({ ...previous, [name]: value }))
                    }
                    dynamicAmenities={dynamicAmenities.length > 0 ? dynamicAmenities : undefined}
                    isLoading={isFetchingAmenities}
                  />
                  <p className="amenities-hint"> {t('amenitiesTooltip')}</p>

                  <div className="divider" />
                  <div className="station-basic-details documents-section">
                    <div className="documents-header">
                      <p className="title">{t('documents')}</p>
                      {additionalDocuments.length < 2 && (
                        <button
                          type="button"
                          className="add-document-btn"
                          onClick={() => setAdditionalDocuments([...additionalDocuments, []])}>
                          + {t('add', 'Add')}
                        </button>
                      )}
                    </div>

                    <div className="documents-edit-grid">
                      {/* LEFT */}
                      <div className="doc-col">
                        <CustomFileInput
                          inputLabel={t('landDocument', 'Land Document')}
                          name="editLandOwnerShipDocument"
                          acceptedFileType="image/*, application/pdf"
                          placeholder={t('uploadPdf')}
                          selectedFiles={editData.editLandOwnerShipDocument}
                          onChangeValue={handleInputChange}
                          existingFiles={chargingStationData?.landOwnerShipDocument}
                          unselectHandler={(name, fileUrl, index, type) =>
                            handleRemoveExistingFile('landOwnerShipDocument', fileUrl, index, type)
                          }
                          valueRequired={false}
                          maxFileSizeMB={1}
                          error={
                            editData.editLandOwnerShipDocument?.length > 0 &&
                            !checkFileSizeValidation(editData.editLandOwnerShipDocument, 1, 1)
                          }
                          errorMessage={t(
                            'documentsizelessthan1mb',
                            'File size should not exceed 1MB',
                          )}
                        />
                      </div>

                      {/* RIGHT */}
                      <div className="doc-col">
                        <CustomFileInput
                          inputLabel={t('ebReport', 'EB Report')}
                          name="editEbReport"
                          acceptedFileType="image/*, application/pdf"
                          placeholder={t('uploadPdf')}
                          selectedFiles={editData.editEbReport}
                          onChangeValue={handleInputChange}
                          existingFiles={chargingStationData?.ebReport}
                          unselectHandler={(name, fileUrl, index, type) =>
                            handleRemoveExistingFile('ebReport', fileUrl, index, type)
                          }
                          valueRequired={false}
                          maxFileSizeMB={1}
                          error={
                            editData.editEbReport?.length > 0 &&
                            !checkFileSizeValidation(editData.editEbReport, 1, 1)
                          }
                          errorMessage={t(
                            'documentsizelessthan1mb',
                            'File size should not exceed 1MB',
                          )}
                        />
                      </div>

                      {/* Additional Documents */}
                      {additionalDocuments.map((files, index) => (
                        <div key={index} className="additional-document-field-wrap">
                          <p className="doc-label">
                            <span className="label-with-tooltip">{`${t('additionalDocument', 'Additional Document')} ${index + 1}`}</span>
                          </p>
                          <CustomFileInput
                            acceptedFileType="image/*, application/pdf"
                            placeholder={t('uploadPdf')}
                            name={`additionalDocument-${index}`}
                            selectedFiles={files}
                            onChangeValue={(name, selectedFileList) => {
                              const updated = [...additionalDocuments];
                              updated[index] = selectedFileList as any;
                              setAdditionalDocuments(updated);
                            }}
                            valueRequired={false}
                            error={false}
                            errorMessage={t('documentRequiredError')}
                          />
                          <button
                            type="button"
                            className="remove-document-btn"
                            onClick={() => {
                              // eslint-disable-next-line unicorn/prevent-abbreviations
                              setAdditionalDocuments(
                                additionalDocuments.filter((_, index_) => index_ !== index),
                              );
                            }}>
                            {t('remove', 'Remove')}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
            {openTab === 1 && (
              <div className="operating-hours-container">
                <div className="timezone-section">
                  <SelectField
                    inputLabel={t('timeZone')}
                    name="timeZone"
                    placeholder={t('selectTimeZone')}
                    selectedValue={editData.timeZone || ''}
                    onChangeValue={(name, value) =>
                      setEditData((previous: any) => ({
                        ...previous,
                        [name]: normalizeTimeZone(value as string),
                      }))
                    }
                    options={timeZoneOptions}
                    enableSearch
                  />
                </div>

                <div className="opening-hours-section">
                  <div className="section-header">
                    <p className="section-title"> </p>
                    <div className="switch-buttons-group">
                      <div className="switch-button">
                        <p>{t('24x7')}</p>
                        <div>
                          <CustomInputSwitch
                            inputLabel=""
                            name="allDay"
                            value={editData.operatingHours?.allDay}
                            onChangeValue={handleAllDayToggle}
                          />{' '}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="days-config-layout">
                    {[
                      'monday',
                      'tuesday',
                      'wednesday',
                      'thursday',
                      'friday',
                      'saturday',
                      'sunday',
                    ].map((day) => {
                      const dayData = editData.operatingHours?.[day as keyof OperatingHours] as any;
                      return (
                        <div className="day-config-row" key={day}>
                          <div className="day-header-inline">
                            <div className="day-toggle-name">
                              <CustomInputSwitch
                                name={day}
                                value={dayData?.isOpen}
                                onChangeValue={(name, value) =>
                                  handleDayToggle(name as DayKey, value)
                                }
                              />
                              <p className="day-name">{t(day).slice(0, 3)}</p>
                            </div>

                            {dayData?.isOpen ? (
                              <div className="day-controls-inline">
                                <div className="twenty-four-hour-toggle">
                                  <CustomInputSwitch
                                    name={`${day}-24h`}
                                    inputLabel=""
                                    value={dayData?.is24Hours || false}
                                    onChangeValue={(_n, value) => {
                                      if (value) {
                                        handleOperatingHoursChange(
                                          day as DayKey,
                                          'is24Hours',
                                          true,
                                        );
                                        handleOperatingHoursChange(day as DayKey, 'from', '00:00');
                                        handleOperatingHoursChange(day as DayKey, 'to', '23:59');
                                      } else {
                                        handleOperatingHoursChange(
                                          day as DayKey,
                                          'is24Hours',
                                          false,
                                        );
                                        handleOperatingHoursChange(day as DayKey, 'from', '09:00');
                                        handleOperatingHoursChange(day as DayKey, 'to', '18:00');
                                      }
                                    }}
                                  />
                                  <p className="twenty-four-label">{t('24 Hours')}</p>
                                </div>
                                <div className="time-field-wrap">
                                  <CustomTimePicker
                                    innerLabel={t('from')}
                                    name={`${day}-from`}
                                    value={dayData?.from}
                                    disabled={dayData?.is24Hours}
                                    onChangeValue={(_n, v) =>
                                      handleOperatingHoursChange(day as DayKey, 'from', v)
                                    }
                                  />
                                </div>
                                <div className="time-field-wrap">
                                  <CustomTimePicker
                                    innerLabel={t('to')}
                                    name={`${day}-to`}
                                    value={dayData?.to}
                                    disabled={dayData?.is24Hours}
                                    onChangeValue={(_n, v) =>
                                      handleOperatingHoursChange(day as DayKey, 'to', v)
                                    }
                                  />
                                </div>
                              </div>
                            ) : (
                              <div className="day-summary">
                                <span className="status-text closed">{t('closed')}</span>
                                <span className="time-range">—</span>
                              </div>
                            )}
                          </div>

                          {dayData?.isOpen &&
                            dayData.from &&
                            dayData.to &&
                            dayData.from >= dayData.to &&
                            !editData.operatingHours?.allDay && (
                              <p
                                className="text-danger m-0 pt-1"
                                style={{ fontSize: '12px', paddingLeft: '140px' }}>
                                {t('closeTimeGreaterThanOpenTime')}
                              </p>
                            )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {isEdit && (
        <div className="station-details-footer">
          <div className="footer-actions-edit">
            <MyButton
              label={t('cancel', 'Cancel')}
              buttonType="outline"
              onClickFunc={toggleEditMode}
            />
            <MyButton
              label={t('submit', 'Submit')}
              buttonType="submit"
              onClickFunc={handleSubmitStationDetails}
              loading={formStatus.buttonLoader}
              disableButton={formStatus.buttonLoader}
            />
          </div>
        </div>
      )}

      <div
        className={`station-panel-device-view-wrap ${selectedDeviceId ? 'active' : ''}`}
        onClick={(event) => event.stopPropagation()}
        role="presentation">
        <div className="side-panel-header">
          <div />
          <span role="presentation" onClick={handleCloseDeviceOverlay}>
            <CloseIcon />
          </span>
        </div>
        {selectedDeviceId && (
          <div className="side-panel-content">
            <DeviceSidePanel
              fetchIdFrom="props"
              id={selectedDeviceId}
              onClose={handleCloseDeviceOverlay}
              showCloseIcon={false}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default StationSidePanel;
